const GRAVITY_METERS_PER_SECOND_SQUARED = 9.80665;

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

function normalizeAngleDeg(angle) {
  let normalized = angle;
  while (normalized > 180) normalized -= 360;
  while (normalized < -180) normalized += 360;
  return normalized;
}

function computeTrackHeadingDeg(firstPoint, secondPoint) {
  const firstLatitude = (firstPoint.lat * Math.PI) / 180;
  const secondLatitude = (secondPoint.lat * Math.PI) / 180;
  const longitudeDifference = ((secondPoint.lon - firstPoint.lon) * Math.PI) / 180;
  const y = Math.sin(longitudeDifference) * Math.cos(secondLatitude);
  const x = (Math.cos(firstLatitude) * Math.sin(secondLatitude))
    - (Math.sin(firstLatitude) * Math.cos(secondLatitude) * Math.cos(longitudeDifference));
  return (Math.atan2(y, x) * 180) / Math.PI;
}

function readPointSpeedKmh(point) {
  const candidates = [point?.speedKmh, point?.speed, point?.velocidade];
  for (const candidate of candidates) {
    const speedKmh = Number(candidate);
    if (Number.isFinite(speedKmh) && speedKmh >= 0) return speedKmh;
  }
  return null;
}

function resolveTurnSpeedKmh(points, pointIndex) {
  const speeds = [];
  for (let index = Math.max(0, pointIndex - 1); index <= Math.min(points.length - 1, pointIndex + 1); index++) {
    const speedKmh = readPointSpeedKmh(points[index]);
    if (speedKmh !== null) speeds.push(speedKmh);
  }
  if (!speeds.length) return null;
  return speeds.reduce((sum, speedKmh) => sum + speedKmh, 0) / speeds.length;
}

export function calculateTurnBankSeries(points, options = {}) {
  const bankSeries = new Array(points.length).fill(0);
  if (points.length < 3) return bankSeries;

  const maxDeg = Math.max(1, Number(options.maxDeg) || 35);
  const factor = Number.isFinite(Number(options.factor)) ? Number(options.factor) : 1;
  const deadzoneDegPerSec = Math.max(0, Number(options.deadzoneDegPerSec) || 0);
  const minimumSpeedKmh = Math.max(0, Number(options.minimumSpeedKmh) || 0);
  const sign = Number(options.sign) < 0 ? -1 : 1;
  const smoothing = clamp(Number.isFinite(Number(options.smoothing)) ? Number(options.smoothing) : 0.5, 0, 0.95);
  const smoothingAlpha = clamp(1 - smoothing, 0.05, 1);
  const segmentHeadings = [];

  for (let index = 0; index < points.length - 1; index++) {
    segmentHeadings.push(computeTrackHeadingDeg(points[index], points[index + 1]));
  }

  const targetBank = new Array(points.length).fill(0);
  for (let index = 1; index < points.length - 1; index++) {
    const elapsedSeconds = Math.max(0.05, Number(points[index + 1].tSec) - Number(points[index - 1].tSec));
    const turnRateDegPerSec = normalizeAngleDeg(segmentHeadings[index] - segmentHeadings[index - 1]) / elapsedSeconds;
    const speedKmh = resolveTurnSpeedKmh(points, index);

    if (Math.abs(turnRateDegPerSec) <= deadzoneDegPerSec || (speedKmh !== null && speedKmh < minimumSpeedKmh)) {
      continue;
    }

    const speedMetersPerSecond = speedKmh === null ? 0 : speedKmh / 3.6;
    const turnRateRadPerSec = (turnRateDegPerSec * Math.PI) / 180;
    const physicalBankDeg = speedMetersPerSecond > 0
      ? (Math.atan((speedMetersPerSecond * Math.abs(turnRateRadPerSec)) / GRAVITY_METERS_PER_SECOND_SQUARED) * 180) / Math.PI
      : Math.abs(turnRateDegPerSec);
    targetBank[index] = clamp(Math.sign(turnRateDegPerSec) * physicalBankDeg * factor * sign, -maxDeg, maxDeg);
  }

  for (let index = 1; index < points.length; index++) {
    bankSeries[index] = bankSeries[index - 1] + smoothingAlpha * (targetBank[index] - bankSeries[index - 1]);
  }
  for (let index = points.length - 2; index >= 0; index--) {
    bankSeries[index] = bankSeries[index + 1] + smoothingAlpha * (bankSeries[index] - bankSeries[index + 1]);
  }

  bankSeries[0] = 0;
  bankSeries[bankSeries.length - 1] = 0;
  return bankSeries.map((bankDeg) => clamp(bankDeg, -maxDeg, maxDeg));
}
